import { computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import { useTemplateStore } from "@stores/mailing/template";
import { useMCampaignStore } from "@stores/mCampaign";
import { usePricerProjectStore } from "@stores/pricer/pricerProject";
import { useJobsStore } from "@stores/job/jobs";
import { useMailingListStore } from "@stores/survey/mailing/mailingList";
import { useEmailCampaignStore } from "@stores/survey/mailing/emailCampaign";
import { useMasterReportsStore } from "@stores/masterReports/masterReports";
import { useReportThemesStore } from "@stores/reportThemes/reportThemes";
import { useChatPagesStore } from "@stores/chatPages";
import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
import { useJcdCampaignsStore } from "@stores/jcdCampaigns";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useExportsStore } from "@stores/survey/exports";
import { useCurrentUserStore } from "@stores/currentUser";
import { useAdTemplatesStore } from "@stores/adTemplates";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface PageHeading {
  title: BreadcrumbItem | null;
  trail: BreadcrumbItem[];
  status?: string | null;
}

const EMPTY: PageHeading = { title: null, trail: [] };

const crumb = (label: string, to?: string): BreadcrumbItem => ({ label, to });

export function useBreadcrumbs() {
  const route = useRoute();

  const { survey } = storeToRefs(useSurveyStore());
  const { template } = storeToRefs(useTemplateStore());
  const { currentMCampaign } = storeToRefs(useMCampaignStore());
  const { currentJob } = storeToRefs(useJobsStore());
  const { mailingList } = storeToRefs(useMailingListStore());
  const { emailCampaign } = storeToRefs(useEmailCampaignStore());
  const { project } = storeToRefs(usePricerProjectStore());
  const { currentMasterReport } = storeToRefs(useMasterReportsStore());
  const { currentTheme: currentReportTheme } = storeToRefs(useReportThemesStore());
  const { customizedChatPage } = storeToRefs(useChatPagesStore());
  const { currentCampaign: currentJcdCampaign } = storeToRefs(useJcdCampaignsStore());
  const { currentReport: currentReportV2 } = storeToRefs(useReportsV2Store());
  const { currentView: resultsV2View, isDefaultView: resultsV2IsDefault } = storeToRefs(useResultsV2Store());
  const { user } = storeToRefs(useCurrentUserStore());
  const { template: adTemplate } = storeToRefs(useAdTemplatesStore());
  const exportsStore = useExportsStore();

  const heading = computed<PageHeading>(() => {
    const path = route.path;
    const routeName = route.name as string;

    if (path === "/surveys/new") return { title: crumb("Surveys", "/"), trail: [crumb("New")] };

    // Survey routes: /surveys/:token/...
    if (path.match(/^\/surveys\/[^/]+/)) {
      const surveyName = survey.value?.name || "...";
      const surveyToken = route.params.token as string;
      const base = `/surveys/${surveyToken}`;
      const home = `${base}/${user.value ? "questions" : "dashboards"}`;
      const status = survey.value?.status ?? null;
      const withTrail = (trail: BreadcrumbItem[]): PageHeading => ({ title: crumb(surveyName, home), trail, status });

      switch (routeName) {
        case "SurveyResultsV2":
        case "SurveyResultsV2View": {
          const viewLabel = resultsV2IsDefault.value ? "Default dashboard" : resultsV2View.value?.name || "";
          return withTrail(viewLabel ? [crumb(viewLabel)] : []);
        }
        case "MailingListEdit":
          return withTrail([crumb("Lists", `${base}/mailing/lists`), crumb(mailingList.value?.name || "...")]);
        case "MailingListNew":
          return withTrail([crumb("Lists", `${base}/mailing/lists`), crumb("New")]);
        case "EmailCampaignEdit":
          return withTrail([
            crumb("Campaigns", `${base}/mailing/campaigns`),
            crumb(emailCampaign.value?.name || "..."),
          ]);
        case "EmailCampaignNew":
          return withTrail([crumb("Campaigns", `${base}/mailing/campaigns`), crumb("New")]);
        case "SurveyExportNew":
          return withTrail([crumb("Exports", `${base}/exports`), crumb("New")]);
        case "SurveyExportEdit": {
          const surveyExport = exportsStore.findByToken(route.params.exportToken as string);
          return withTrail([crumb("Exports", `${base}/exports`), crumb(surveyExport?.name || "...")]);
        }
        case "ReportV2New":
          return withTrail([crumb("Reports", `${base}/reports`), crumb("New")]);
        case "ReportV2Settings":
        case "ReportV2Slides": {
          const reportPath = `${base}/reports/${route.params.reportToken}/settings`;
          return withTrail([
            crumb("Reports", `${base}/reports`),
            crumb(currentReportV2.value?.name || "...", reportPath),
            crumb(routeName === "ReportV2Slides" ? "Slides" : "Setup"),
          ]);
        }
        default:
          return withTrail([]);
      }
    }

    if (path.match(/^\/lm_surveys\/[^/]+/)) return { title: crumb(survey.value?.name || "..."), trail: [] };
    if (path.match(/^\/mailing\/templates\/[^/]+/)) return { title: crumb(template.value?.name || "..."), trail: [] };
    if (path.match(/^\/admin\/m_campaigns\/[^/]+/))
      return { title: crumb(currentMCampaign.value?.name || "..."), trail: [] };
    if (path.match(/^\/admin\/pricer\/projects\/[^/]+/))
      return { title: crumb(project.value?.name || "..."), trail: [] };
    if (path.match(/^\/admin\/ad-templates\/[^/]+/))
      return { title: crumb("Ad templates", "/admin/ad-templates"), trail: [crumb(adTemplate.value?.name || "...")] };
    if (path.match(/^\/master-reports\/new$/))
      return { title: crumb("Master Reports", "/master-reports"), trail: [crumb("New")] };
    if (path.match(/^\/master-reports\/[^/]+/))
      return { title: crumb(currentMasterReport.value?.name || "..."), trail: [] };

    if (routeName === "JobEdit") return { title: crumb(currentJob.value?.name || "..."), trail: [] };
    if (routeName === "JobForm") return { title: crumb("Jobs", "/admin/jobs"), trail: [crumb("New")] };

    if (path.match(/^\/chat-pages\/[^/]+/)) return { title: crumb(customizedChatPage.value?.name || "..."), trail: [] };
    if (path.match(/^\/report-themes\/[^/]+/))
      return { title: crumb(currentReportTheme.value?.name || "..."), trail: [] };

    if (path === "/jcd-campaigns/new")
      return { title: crumb("Campagnes JCD", "/jcd-campaigns"), trail: [crumb("Nouvelle campagne")] };
    if (path.match(/^\/jcd-campaigns\/[^/]+/)) {
      const campaign = currentJcdCampaign.value;
      return { title: crumb(campaign ? `${campaign.brand} — S${campaign.week} ${campaign.year}` : "..."), trail: [] };
    }

    return EMPTY;
  });

  return { heading };
}
